import { PropsWithChildren, ReactNode, useEffect, useState } from "react";
import { Body1Stronger, Button, Caption1, Caption1Stronger, Card, CardFooter, CardHeader, Spinner, makeStyles, tokens } from "@fluentui/react-components";
import {
    OpenRegular,
    CheckmarkCircle16Filled,
    DismissCircle16Regular,
    Circle16Regular,
    ArrowSyncRegular
} from "@fluentui/react-icons";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";
import { IM365EntraIDTileProps } from "./m365-entra-id-tile.props";
import Trimmed from "components/common/trimmed/trimmed";
import { PPAPermissions } from "lib/permissions";
import { getConsentUrl } from "lib/consent";
import { useTeams } from "@microsoft/teamsfx-react";
import { authentication } from "@microsoft/teams-js";
import { IM365Permission } from "@interfaces/settings/permissions.interfaces";

const layoutStyles = makeStyles({
    resourceWrapper: {
        marginBottom: '1em'
    },
    permissionWrapper: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '0.25em'
    },
    statusIconWrapper: {
        width: '16px'
    },
    permissionText: {
        marginLeft: '0.25em',
        overflow: 'hidden',
        textWrap: 'nowrap',
        whiteSpace: 'none',
        textOverflow: 'ellipsis'
    },
    description: {
        fontSize: tokens.fontSizeBase200,
        lineHeight: 'normal'
    },
    body: {
        fontSize: tokens.fontSizeBase200,
        lineHeight: 'normal'
    },
    error: {
        color: tokens.colorStatusDangerForeground1
    },
    leftButtons: {
        flexGrow: 1
    }
});

export default function M365EntraIDTile(props: PropsWithChildren<IM365EntraIDTileProps>) {
    const styles = layoutStyles();
    const { t } = useL10n<ILocaleStrings>();
    const [{ context }] = useTeams();
    const [consentOpen, setConsentOpen] = useState<boolean>(false);
    const [checking, setChecking] = useState<boolean>(false);
    const [consentError, setConsentError] = useState<string>();

    useEffect(() => {
        setChecking(props.checking);
    }, [props.checking]);

    const setConsentErrorMessage = (errorReason: any) => {
        try {
            if (typeof errorReason === "string") {
                setConsentError(errorReason);
            }
            else if (errorReason?.message && typeof errorReason.message === "string") {
                const errorString = errorReason.message;
                if (errorString.indexOf('CancelledByUser') > -1) {
                    // Cancelled by closing the consent dialog. This is fine.
                    setConsentError('');
                }
                else if (errorString.indexOf('AADSTS65004') > -1) {
                    // Cancelled via the cancel button on the consent dialog. This is fine.
                    setConsentError('');
                }
                else if (errorString.indexOf('errorDescription') > -1) {
                    var errorObject = JSON.parse(errorString);
                    setConsentError(errorObject?.errorDescription);
                }
            }
            else {
                // Something else...
                setConsentError(t("error"));
            }
        }
        catch (ex: any) {
            console.log(`setConsentErrorMessage ->`, ex);
        }
    }

    if (!props.m365EntraIDPermissions)
        return <></>

    const openConsent = () => {
        setConsentOpen(true);
        props.setConsentCallback();
        setConsentError('');
        authentication.authenticate({
            url: getConsentUrl(context?.user?.tenant?.id, props.m365EntraIDPermissions?.clientId),
            width: 500,
            height: 650
        })
            .then(
                (result: any) => {
                    console.log(`authenticate -> result ->`, result);
                    props.setConsentedCallback(new Date().toISOString());
                    setConsentOpen(false);
                }
            )
            .catch((reason: any) => {
                console.log(`authenticate -> catch ->`, reason);
                props.setConsentedCallback('');
                setConsentOpen(false);
                setConsentErrorMessage(reason);
            });
    }

    const refresh = () => {
        setConsentError('');
        props.refreshCallback();
    }

    function Permission(props: IM365Permission) {
        let icon: ReactNode = <></>;

        if (checking)
            icon = <Spinner size="extra-tiny" />;
        else if (props.required && props.consented)
            icon = <CheckmarkCircle16Filled style={{ color: tokens.colorPaletteLightGreenBackground3 }} />;
        else if (props.required && !props.consented)
            icon = <Circle16Regular style={{ color: tokens.colorPaletteMarigoldForeground1 }} />;
        else if (!props.required && props.consented)
            icon = <DismissCircle16Regular style={{ color: tokens.colorNeutralForeground4 }} />;

        return (
            <div className={styles.permissionWrapper} >
                <div className={styles.statusIconWrapper}>
                    {icon}
                </div>
                <Caption1 className={styles.permissionText} >
                    {props.value} - {props.name}
                </Caption1>
            </div>
        );
    }

    return (
        <Card>
            <CardHeader
                header={<Body1Stronger>{props.m365EntraIDPermissions.appName}</Body1Stronger>}
                description={
                    <span
                        className={styles.description}
                        dangerouslySetInnerHTML={{
                            __html: t('ppaM365EntraIDAppDescription',
                                {
                                    appName: props.m365EntraIDPermissions.appName,
                                    clientId: props.m365EntraIDPermissions.clientId
                                })
                        }}></span>} />
            <div className={styles.body}>
                {props.m365EntraIDPermissions.resources.map((resource, idx: number) => (
                    <div key={`r-${idx}`} className={styles.resourceWrapper}>
                        <Caption1Stronger>{resource.name}</Caption1Stronger>
                        {resource.permissions.map((permission, idx: number) => (
                            <Permission key={`p-${idx}`} {...permission} />
                        ))}
                    </div>
                ))}
            </div>
            <div>
                <Caption1 className={styles.error}>{consentError}</Caption1>
            </div>
            <CardFooter>
                <div className={styles.leftButtons}>
                    <Trimmed permission={PPAPermissions.ConsentMicrosoftEntraIDPermissions}>
                        <Button
                            disabled={props.checking || consentOpen}
                            icon={<OpenRegular />}
                            iconPosition="after"
                            onClick={openConsent}>{t('consent')}</Button>
                    </Trimmed>
                </div>
                <Button
                    disabled={props.checking || consentOpen}
                    icon={<ArrowSyncRegular />}
                    onClick={refresh}></Button>
            </CardFooter>
        </Card>
    );
}